<template>
  <section
    class="header-dashboard-cards"
    :class="[variantClass, columnsClass]"
  >
    <component
      :is="card.clickable ? 'button' : 'div'"
      v-for="card in metrics"
      :key="card.key"
      class="header-dashboard-cards__card"
      :class="[
        { 'header-dashboard-cards__card--clickable': card.clickable },
        cardClasses(card),
      ]"
      :type="card.clickable ? 'button' : null"
      :aria-pressed="card.clickable ? card.active : null"
      :aria-label="card.clickable ? cardAriaLabel(card) : null"
      @click="onCardClick(card)"
    >
      <span
        v-if="hasIcon(card)"
        class="header-dashboard-cards__icon"
        aria-hidden="true"
      >
        <svg
          v-for="path in card.icon"
          :key="path"
          class="header-dashboard-cards__svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            :d="path"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
      <span
        v-if="variant === 'count-only'"
        class="header-dashboard-cards__label"
      >{{ card.title }}</span>
      <span
        class="header-dashboard-cards__count"
        :class="{ 'header-dashboard-cards__count--red': isRedCount(card) }"
      >{{ card.count }}</span>
      <span
        v-if="variant === 'icon'"
        class="header-dashboard-cards__title"
      >{{ card.title }}</span>
      <span
        v-if="variant === 'icon' && card.clickable"
        class="header-dashboard-cards__action"
      >{{ actionLabel }}</span>
    </component>
  </section>
</template>

<script setup>
import { computed } from "vue";

/**
 * Shared card grid behind the Organization sub-tab header dashboards
 * (Employees / Positions / Roles / Departments — and Responsibilities once
 * ER-10524 lands). Two visual variants:
 *   - `icon`: icon box + count + title (+ action text when clickable)
 *   - `count-only`: label above a 36px count, no icon and no visible action text
 *
 * Each metric item:
 *   { key, title, count, icon?: string[], clickable?: boolean, alert?: boolean, isRed?: boolean, active?: boolean }
 * `isRed` colours that card's count red (per-card rule); `alert` also tints the icon box.
 */
const props = defineProps({
  metrics: {
    type: Array,
    required: true,
  },
  variant: {
    type: String,
    default: "icon",
    validator: (value) => ["icon", "count-only"].includes(value),
  },
  actionLabel: {
    type: String,
    default: "Apply Filter",
  },
  /** Number of desktop grid columns (Roles uses 5; others default to 4). */
  columns: {
    type: Number,
    default: 4,
  },
});

const emit = defineEmits(["select"]);

const variantClass = computed(() =>
  props.variant === "count-only" ? "header-dashboard-cards--count-only" : "",
);

const columnsClass = computed(() =>
  props.columns === 5 ? "header-dashboard-cards--columns-5" : "",
);

function hasIcon(card) {
  return props.variant === "icon" && Array.isArray(card.icon) && card.icon.length > 0;
}

function isRedCount(card) {
  return Boolean(card.isRed);
}

function cardClasses(card) {
  return {
    "header-dashboard-cards__card--active": Boolean(card.active),
    "header-dashboard-cards__card--alert": Boolean(card.alert),
  };
}

function cardAriaLabel(card) {
  return `${card.title} (${card.count}) — ${props.actionLabel}`;
}

function onCardClick(card) {
  if (card.clickable) {
    emit("select", card.key);
  }
}
</script>

<style scoped>
.header-dashboard-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
}

.header-dashboard-cards--columns-5 {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.header-dashboard-cards__card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #eaecf0;
  background: #ffffff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  width: 100%;
}

.header-dashboard-cards__card--clickable {
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  color: inherit;
}

.header-dashboard-cards__card--clickable:hover {
  border-color: #d0d5dd;
}

.header-dashboard-cards__card--clickable:focus-visible {
  outline: 2px solid #6941c6;
  outline-offset: 2px;
}

/* Selected state is a light fill only — no purple stroke or ring (ER-12336). */
.header-dashboard-cards__card--active {
  background: #f9f5ff;
}

.header-dashboard-cards--count-only .header-dashboard-cards__card {
  gap: 0;
  padding: 24px;
  min-width: 200px;
}

.header-dashboard-cards__icon {
  display: flex;
  width: 40px;
  height: 40px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  border: 1px solid #eaecf0;
  background: #ffffff;
  color: #6941c6;
}

.header-dashboard-cards__card--alert .header-dashboard-cards__icon {
  border-color: #fda29b;
  background: #fef3f2;
  color: #b42318;
}

.header-dashboard-cards__svg {
  display: block;
}

.header-dashboard-cards__count {
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 28px;
  line-height: 36px;
  color: #101828;
}

.header-dashboard-cards--count-only .header-dashboard-cards__count {
  font-weight: 600;
  font-size: 36px;
  line-height: 44px;
}

.header-dashboard-cards__count--red {
  color: #d92d20;
}

.header-dashboard-cards__card--alert .header-dashboard-cards__count {
  color: #b42318;
}

.header-dashboard-cards__title {
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}

.header-dashboard-cards__label {
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
}

.header-dashboard-cards--count-only .header-dashboard-cards__label {
  margin-bottom: 8px;
}

.header-dashboard-cards__action {
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 13px;
  line-height: 20px;
  color: #6941c6;
}

@media (max-width: 1400px) {
  .header-dashboard-cards--columns-5 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1200px) {
  .header-dashboard-cards:not(.header-dashboard-cards--columns-5) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .header-dashboard-cards--columns-5 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .header-dashboard-cards,
  .header-dashboard-cards--columns-5 {
    grid-template-columns: 1fr;
  }
}
</style>
